import { Dispatch, SetStateAction } from "react"
import { SpinnerText } from "../ui/Spinner"
import InfoBox from "../ui/InfoBox"
import QuestionBox from "../ui/QuestionBox"
import { ProductIndicationTerm } from "../../services/productIndicationService"

type ProductIndicationSectionProps = {
  productIndication: string
  setProductIndication: (value: string) => void
  productIndicationResults: ProductIndicationTerm[]
  setProductIndicationResults: Dispatch<SetStateAction<ProductIndicationTerm[]>>
  selectedProductIndication: ProductIndicationTerm | null
  setSelectedProductIndication: (value: ProductIndicationTerm | null) => void
  addedProductIndications: ProductIndicationTerm[]
  setAddedProductIndications: Dispatch<SetStateAction<ProductIndicationTerm[]>>
  productIndicationValidation: Record<string, boolean | null>
  productIndicationLoading: boolean
  productIndicationError: string
  formatProductIndication: (term: ProductIndicationTerm) => string
  handleAddProductIndication: () => void
}

function getProductIndicationKey(term: ProductIndicationTerm) {
  return `${term.text}|${term.classNumber}|${term.subClassNumber}`
}

export default function ProductIndicationSection({
  productIndication,
  setProductIndication,
  productIndicationResults,
  setProductIndicationResults,
  selectedProductIndication,
  setSelectedProductIndication,
  addedProductIndications,
  setAddedProductIndications,
  productIndicationValidation,
  productIndicationLoading,
  productIndicationError,
  formatProductIndication,
  handleAddProductIndication
}: ProductIndicationSectionProps) {
  const groupedProductIndications = addedProductIndications.reduce((groups, term) => {
    const key = `${term.classNumber}-${term.subClassNumber}`
    if (!groups[key]) groups[key] = []
    groups[key].push(term)
    return groups
  }, {} as Record<string, ProductIndicationTerm[]>)

  return (
    <div className="border-b-2 border-dashed border-gray-300 py-6">
      <div className="mb-3 flex items-center gap-2">
        <h4 className="text-lg font-semibold text-blue-800">
          Product indication*
        </h4>
        <QuestionBox title="Product indication">
          A design application must include the indication of the products in
          which the design is intended to be incorporated or to which it is
          intended to be applied. The product indication should clearly identify
          the nature of the product and preferably use terms from the Locarno
          Classification.
        </QuestionBox>
      </div>

      <p className="mb-2 text-sm text-gray-800">
        Search and select terms from the Harmonised Database
      </p>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={productIndication}
            onChange={(event) => {
              setProductIndication(event.target.value)
              setSelectedProductIndication(null)
            }}
            className="w-full rounded border-2 border-blue-800 px-3 py-2 text-sm italic focus:outline-none focus:ring-1 focus:ring-blue-800"
            placeholder="Search by class, subclass or product indication"
          />

          {(productIndicationResults.length > 0 ||
            productIndicationLoading ||
            productIndicationError) && (
            <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto border-2 border-blue-800 bg-white text-sm shadow-sm">
              {productIndicationLoading && (
                <div className="px-4 py-2 text-gray-500">
                  <SpinnerText>Loading...</SpinnerText>
                </div>
              )}

              {productIndicationError && (
                <div className="px-4 py-2 text-red-600">
                  {productIndicationError}
                </div>
              )}

              {!productIndicationLoading &&
                !productIndicationError &&
                productIndicationResults.map((term) => (
                  <button
                    key={`${term.conceptId ?? term.text}-${term.classNumber}-${term.subClassNumber}`}
                    type="button"
                    onClick={() => {
                      setSelectedProductIndication(term)
                      setProductIndication(formatProductIndication(term))
                      setProductIndicationResults([])
                    }}
                    className="block w-full border-b border-gray-200 px-4 py-2 text-left text-gray-800 last:border-b-0 hover:bg-blue-50"
                  >
                    {formatProductIndication(term)}
                  </button>
                ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleAddProductIndication}
          disabled={!selectedProductIndication}
          className={`rounded border-2 px-6 py-2 text-sm font-semibold uppercase text-white ${
            selectedProductIndication
              ? "border-blue-800 bg-blue-800 hover:bg-blue-700"
              : "border-gray-500 bg-gray-400 cursor-not-allowed"
          }`}
        >
          Add
        </button>
      </div>

      {addedProductIndications.length > 0 && (
        <div className="mt-4 rounded border border-gray-300 bg-gray-50 p-4 text-sm">
          <div className="mb-3 flex items-center justify-end gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="h-2 w-10 rounded-sm bg-green-500"></div>
              <span className="font-medium uppercase tracking-wide text-gray-800">
                VALID IN HDB
              </span>
              <span className="text-gray-600">
                {Object.values(productIndicationValidation).filter(value => value === true).length}
              </span>
              <InfoBox title="Valid in HDB">
                These terms are officially part of the Harmonized Database.
              </InfoBox>
            </div>

            <div className="flex items-center gap-2">
              <div className="h-2 w-10 rounded-sm bg-orange-400"></div>
              <span className="font-medium uppercase tracking-wide text-gray-800">
                NOT VALID
              </span>
              <span className="text-gray-600">
                {Object.values(productIndicationValidation).filter(value => value === false).length}
              </span>
              <InfoBox title="Review suggestions">
                Orange terms are not valid in the Harmonized Database.
              </InfoBox>
            </div>
          </div>

          {Object.entries(groupedProductIndications).map(([key, terms]) => {
            const [classNumber, subClassNumber] = key.split("-")

            return (
              <div
                key={key}
                className="mb-3 rounded border border-blue-100 bg-blue-50 p-2 last:mb-0"
              >
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-semibold text-blue-900">
                    Class {String(classNumber).padStart(2, "0")} / Subclass {String(subClassNumber).padStart(2, "0")}
                  </p>
                </div>

                <div className="flex flex-wrap gap-1">
                  {terms.map((term) => {
                    const status = productIndicationValidation[getProductIndicationKey(term)]
                    let bg = "border-gray-300 bg-gray-100"
                    if (status === true) bg = "border-green-400 bg-green-100"
                    else if (status === false) bg = "border-orange-400 bg-orange-100"
                    const originalIndex = addedProductIndications.findIndex((item) =>
                      item.conceptId
                        ? item.conceptId === term.conceptId
                        : item.text === term.text &&
                          item.classNumber === term.classNumber &&
                          item.subClassNumber === term.subClassNumber
                    )

                    return (
                      <span
                        key={`${term.conceptId ?? term.text}-${term.classNumber}-${term.subClassNumber}`}
                        className={`flex items-center gap-1 rounded border px-2 py-0.5 text-sm ${bg}`}
                      >
                        {term.text}
                        <button
                          type="button"
                          onClick={() =>
                            setAddedProductIndications(prev =>
                              prev.filter((_, itemIndex) => itemIndex !== originalIndex)
                            )
                          }
                          className="text-xs font-semibold text-gray-700 hover:text-blue-800"
                          aria-label="Remove product indication"
                        >
                          X
                        </button>
                      </span>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <p className="mt-3 text-sm text-gray-800">
        If you cannot find terms suitable for your product, you can{" "}
        <button
          type="button"
          onClick={(event) => event.preventDefault()}
          className="font-semibold text-blue-800 underline"
        >
          browse the taxonomy
        </button>
      </p>
    </div>
  )
}
